/**
 * BrandlyHive Agency Theme
 * Main JavaScript
 */

(function() {
  'use strict';

  // =======================================
  // Mobile Menu Toggle
  // =======================================
  function initMobileMenu() {
    const toggle = document.querySelector('.mobile-menu-toggle');
    const mobileNav = document.querySelector('.mobile-nav');
    
    if (!toggle || !mobileNav) return;

    toggle.addEventListener('click', function() {
      mobileNav.classList.toggle('active');
      toggle.classList.toggle('active');
      
      // Prevent body scroll when mobile menu is open
      document.body.style.overflow = mobileNav.classList.contains('active') ? 'hidden' : '';
    });

    // Close mobile menu when clicking on a link
    const mobileLinks = mobileNav.querySelectorAll('a');
    mobileLinks.forEach(link => {
      link.addEventListener('click', function() {
        mobileNav.classList.remove('active');
        toggle.classList.remove('active');
        document.body.style.overflow = '';
      });
    });

    // Close mobile menu when clicking outside
    document.addEventListener('click', function(e) {
      if (!toggle.contains(e.target) && !mobileNav.contains(e.target)) {
        mobileNav.classList.remove('active');
        toggle.classList.remove('active');
        document.body.style.overflow = '';
      }
    });
  }

  // =======================================
  // Smooth Scrolling for Anchor Links
  // =======================================
  function initSmoothScroll() {
    const links = document.querySelectorAll('a[href^="#"]');
    
    links.forEach(link => {
      link.addEventListener('click', function(e) {
        const href = this.getAttribute('href');
        
        // Skip if it's just "#"
        if (href === '#') return;
        
        const target = document.querySelector(href);
        
        if (target) {
          e.preventDefault();
          
          const headerHeight = document.querySelector('.site-header')?.offsetHeight || 0;
          const targetPosition = target.offsetTop - headerHeight;
          
          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          });
        }
      });
    });
  }

  // =======================================
  // Sticky Header on Scroll
  // =======================================
  function initStickyHeader() {
    const header = document.querySelector('.site-header');
    if (!header) return;

    let lastScroll = 0;
    
    window.addEventListener('scroll', function() {
      const currentScroll = window.pageYOffset;
      
      // Add shadow when scrolled
      if (currentScroll > 10) {
        header.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
      } else {
        header.style.boxShadow = '';
      }
      
      lastScroll = currentScroll;
    });
  }

  // =======================================
  // Intersection Observer for Animations
  // =======================================
  function initScrollAnimations() {
    const animatedElements = document.querySelectorAll('[data-animate]');
    
    if (!animatedElements.length) return;

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver(function(entries) {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const delay = entry.target.getAttribute('data-delay') || 0;
          
          setTimeout(() => {
            entry.target.classList.add('animated');
          }, delay);
          
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    animatedElements.forEach(element => {
      observer.observe(element);
    });
  }

  // =======================================
  // Form Validation & Enhancement
  // =======================================
  function initFormEnhancements() {
    const form = document.querySelector('.contact-form');
    if (!form) return;

    // Add loading state on submit
    form.addEventListener('submit', function() {
      const submitButton = form.querySelector('.btn-submit');
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.innerHTML = '<span>Sending...</span>';
      }
    });

    // Real-time validation
    const inputs = form.querySelectorAll('input, textarea');
    inputs.forEach(input => {
      input.addEventListener('blur', function() {
        validateField(this);
      });

      input.addEventListener('input', function() {
        if (this.classList.contains('error')) {
          validateField(this);
        }
      });
    });

    function validateField(field) {
      const value = field.value.trim();
      const isRequired = field.hasAttribute('required');
      
      if (isRequired && !value) {
        field.classList.add('error');
        field.style.borderColor = '#EF4444';
        return false;
      }

      if (field.type === 'email' && value) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          field.classList.add('error');
          field.style.borderColor = '#EF4444';
          return false;
        }
      }

      field.classList.remove('error');
      field.style.borderColor = '';
      return true;
    }
  }

  // =======================================
  // Language Switcher Enhancement
  // =======================================
  function initLanguageSwitcher() {
    const switcher = document.querySelector('.language-switcher');
    if (!switcher) return;

    const button = switcher.querySelector('.language-switcher-button');
    const dropdown = switcher.querySelector('.language-switcher-dropdown');
    
    if (!button || !dropdown) return;

    // Toggle dropdown on button click
    button.addEventListener('click', function(e) {
      e.stopPropagation();
      dropdown.style.display = dropdown.style.display === 'block' ? 'none' : 'block';
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', function() {
      dropdown.style.display = 'none';
    });
  }

  // =======================================
  // Parallax Effect for Hero Shapes
  // =======================================
  function initParallax() {
    const shapes = document.querySelectorAll('.shape');
    if (!shapes.length) return;

    window.addEventListener('scroll', function() {
      const scrolled = window.pageYOffset;
      
      shapes.forEach((shape, index) => {
        const speed = 0.5 + (index * 0.2);
        const yPos = -(scrolled * speed);
        shape.style.transform = `translateY(${yPos}px)`;
      });
    });
  }

  // =======================================
  // Lazy Loading Images
  // =======================================
  function initLazyLoading() {
    const images = document.querySelectorAll('img[loading="lazy"]');
    
    if ('loading' in HTMLImageElement.prototype) {
      // Browser supports native lazy loading
      return;
    }
    
    // Fallback for browsers that don't support lazy loading
    const imageObserver = new IntersectionObserver(function(entries) {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          img.src = img.dataset.src;
          img.classList.add('loaded');
          imageObserver.unobserve(img);
        }
      });
    });

    images.forEach(img => imageObserver.observe(img));
  }

  // =======================================
  // Counter Animation for Stats (if needed)
  // =======================================
  function animateCounter(element, target, duration = 2000) {
    let start = 0;
    const increment = target / (duration / 16);
    
    const timer = setInterval(() => {
      start += increment;
      if (start >= target) {
        element.textContent = target;
        clearInterval(timer);
      } else {
        element.textContent = Math.floor(start);
      }
    }, 16);
  }

  // =======================================
  // Active Navigation Link on Scroll
  // =======================================
  function initActiveNavigation() {
    const sections = document.querySelectorAll('section[id]');
    const navLinks = document.querySelectorAll('.nav-list a[href^="#"]');
    
    if (!sections.length || !navLinks.length) return;

    window.addEventListener('scroll', function() {
      let current = '';
      
      sections.forEach(section => {
        const sectionTop = section.offsetTop;
        const sectionHeight = section.clientHeight;
        
        if (window.pageYOffset >= sectionTop - 200) {
          current = section.getAttribute('id');
        }
      });

      navLinks.forEach(link => {
        link.classList.remove('active');
        if (link.getAttribute('href') === `#${current}`) {
          link.classList.add('active');
        }
      });
    });
  }

  // =======================================
  // Carousel/Slider Functionality
  // =======================================
  function initCarousels() {
    const carousels = document.querySelectorAll('[data-carousel]');
    
    carousels.forEach(carousel => {
      const wrapper = carousel;
      const container = carousel.querySelector('.carousel-container');
      const track = carousel.querySelector('.carousel-track');
      const items = carousel.querySelectorAll('.carousel-item');
      const prevBtn = carousel.querySelector('.carousel-nav-prev');
      const nextBtn = carousel.querySelector('.carousel-nav-next');
      const indicatorsContainer = carousel.querySelector('.carousel-indicators');
      
      if (!track || !items.length) return;

      // Determine visible slides based on screen size
      let visibleSlides;
      if (window.innerWidth <= 768) {
        visibleSlides = 1;
      } else if (window.innerWidth <= 1024) {
        visibleSlides = carousel.classList.contains('partners-carousel') ? 3 : 2;
      } else {
        visibleSlides = parseInt(carousel.getAttribute('data-carousel-slides')) || 3;
      }
      let currentIndex = 0;
      let isTransitioning = false;

      // Create indicators (will be recreated on resize)
      let totalPages = Math.ceil(items.length / visibleSlides);
      
      function createIndicators() {
        if (!indicatorsContainer) return;
        
        // Clear existing indicators
        indicatorsContainer.innerHTML = '';
        
        // Recalculate total pages
        totalPages = Math.ceil(items.length / visibleSlides);
        
        for (let i = 0; i < totalPages; i++) {
          const indicator = document.createElement('button');
          indicator.classList.add('carousel-indicator');
          indicator.setAttribute('aria-label', `Go to slide ${i + 1}`);
          if (i === 0) indicator.classList.add('active');
          indicator.addEventListener('click', () => goToSlide(i));
          indicatorsContainer.appendChild(indicator);
        }
      }
      
      createIndicators();

      // Update carousel position
      function updateCarousel() {
        const itemWidth = items[0].offsetWidth;
        const gap = parseInt(getComputedStyle(track).gap) || 0;
        const offset = -(currentIndex * visibleSlides * (itemWidth + gap));
        track.style.transform = `translateX(${offset}px)`;

        // Update indicators
        const indicators = indicatorsContainer?.querySelectorAll('.carousel-indicator');
        indicators?.forEach((indicator, index) => {
          indicator.classList.toggle('active', index === currentIndex);
        });

        // Update button states
        if (prevBtn) {
          prevBtn.classList.toggle('disabled', currentIndex === 0);
        }
        if (nextBtn) {
          nextBtn.classList.toggle('disabled', currentIndex >= totalPages - 1);
        }
      }

      // Go to specific slide
      function goToSlide(index) {
        if (isTransitioning) return;
        currentIndex = Math.max(0, Math.min(index, totalPages - 1));
        isTransitioning = true;
        updateCarousel();
        setTimeout(() => { isTransitioning = false; }, 500);
      }

      // Next slide
      function nextSlide() {
        if (currentIndex < totalPages - 1) {
          goToSlide(currentIndex + 1);
        }
      }

      // Previous slide
      function prevSlide() {
        if (currentIndex > 0) {
          goToSlide(currentIndex - 1);
        }
      }

      // Event listeners
      if (prevBtn) {
        prevBtn.addEventListener('click', prevSlide);
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', nextSlide);
      }

      // Keyboard navigation
      carousel.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          prevSlide();
        } else if (e.key === 'ArrowRight') {
          nextSlide();
        }
      });

      // Touch/Swipe support for mobile
      let touchStartX = 0;
      let touchEndX = 0;
      let touchStartY = 0;
      let touchEndY = 0;
      let isSwiping = false;

      track.addEventListener('touchstart', (e) => {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        isSwiping = true;
        container.classList.add('dragging');
      }, { passive: true });

      track.addEventListener('touchmove', (e) => {
        if (!isSwiping) return;
        
        touchEndX = e.touches[0].clientX;
        touchEndY = e.touches[0].clientY;
        
        // Prevent vertical scroll if horizontal swipe is detected
        const xDiff = Math.abs(touchEndX - touchStartX);
        const yDiff = Math.abs(touchEndY - touchStartY);
        
        if (xDiff > yDiff && xDiff > 10) {
          e.preventDefault();
        }
      }, { passive: false });

      track.addEventListener('touchend', (e) => {
        if (!isSwiping) return;
        
        isSwiping = false;
        container.classList.remove('dragging');
        handleSwipe();
        
        // Reset values
        touchStartX = 0;
        touchEndX = 0;
        touchStartY = 0;
        touchEndY = 0;
      }, { passive: true });

      function handleSwipe() {
        const swipeThreshold = 50;
        const diff = touchStartX - touchEndX;
        
        if (Math.abs(diff) > swipeThreshold) {
          if (diff > 0) {
            // Swipe left - next slide
            nextSlide();
          } else {
            // Swipe right - previous slide
            prevSlide();
          }
        }
      }

      // Mouse drag support (desktop)
      let isDragging = false;
      let startX = 0;
      let currentX = 0;

      track.addEventListener('mousedown', (e) => {
        isDragging = true;
        startX = e.pageX;
        currentX = e.pageX;
        container.classList.add('dragging');
        track.style.cursor = 'grabbing';
        e.preventDefault();
      });

      document.addEventListener('mousemove', (e) => {
        if (!isDragging) return;
        e.preventDefault();
        currentX = e.pageX;
      });

      document.addEventListener('mouseup', (e) => {
        if (!isDragging) return;
        
        isDragging = false;
        container.classList.remove('dragging');
        track.style.cursor = 'grab';
        
        const diff = startX - currentX;
        const dragThreshold = 50;
        
        if (Math.abs(diff) > dragThreshold) {
          if (diff > 0) {
            // Drag left - next slide
            nextSlide();
          } else {
            // Drag right - previous slide
            prevSlide();
          }
        }
        
        // Reset values
        startX = 0;
        currentX = 0;
      });

      // Prevent text selection during drag
      track.addEventListener('dragstart', (e) => {
        e.preventDefault();
      });

      // Handle window resize
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          // Adjust visible slides on mobile
          if (window.innerWidth <= 768) {
            visibleSlides = 1;
          } else if (window.innerWidth <= 1024) {
            visibleSlides = carousel.classList.contains('partners-carousel') ? 3 : 2;
          } else {
            visibleSlides = parseInt(carousel.getAttribute('data-carousel-slides')) || 3;
          }
          
          // Recreate indicators with new totalPages
          createIndicators();
          
          // Recalculate and update
          currentIndex = 0;
          updateCarousel();
        }, 250);
      });

      // Initial update
      updateCarousel();
    });
  }

  // =======================================
  // Initialize Everything on DOM Ready
  // =======================================
  function init() {
    initMobileMenu();
    initSmoothScroll();
    initStickyHeader();
    initScrollAnimations();
    initFormEnhancements();
    initLanguageSwitcher();
    initParallax();
    initLazyLoading();
    initActiveNavigation();
    initCarousels();
    
    console.log('BrandlyHive Theme initialized');
  }

  // Run initialization
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // =======================================
  // Expose utilities to global scope if needed
  // =======================================
  window.BrandlyHive = {
    animateCounter: animateCounter
  };

})();

